import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Search, LogIn, Trash2, ChevronRight } from "lucide-react";
import { RestaurantDetail } from "./RestaurantDetail";

export function RestaurantsTab({
  adminAxios,
  filteredRestaurants,
  search,
  setSearch,
  selectedRestaurant,
  restaurantDetail,
  detailLoading,
  impersonating,
  auditing,
  fixing,
  blocking,
  auditResult,
  onOpenDetail,
  onCloseDetail,
  onLoginAs,
  onToggleBlock,
  onRunAudit,
  onRunFix,
  onRefreshTab,
}) {
  if (selectedRestaurant) {
    return (
      <RestaurantDetail
        restaurantDetail={restaurantDetail}
        detailLoading={detailLoading}
        impersonating={impersonating}
        auditing={auditing}
        fixing={fixing}
        blocking={blocking}
        auditResult={auditResult}
        onBack={onCloseDetail}
        onLoginAs={onLoginAs}
        onToggleBlock={onToggleBlock}
        onRunAudit={onRunAudit}
        onRunFix={onRunFix}
        adminAxios={adminAxios}
        onRefreshTab={onRefreshTab}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar restaurante..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="admin-search-restaurants"
          />
        </div>
        <Badge variant="outline">{filteredRestaurants.length} restaurantes</Badge>
      </div>
      <ScrollArea className="h-[65vh]">
        <div className="space-y-2">
          {filteredRestaurants.map((r) => (
            <Card key={r.id} className="hover:shadow-md transition-shadow" data-testid={`admin-restaurant-${r.id?.slice(-6)}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-700 font-bold text-sm flex-shrink-0">
                  {r.name?.charAt(0)?.toUpperCase() || "R"}
                </div>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onOpenDetail(r.id)}>
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-sm truncate">{r.name}</p>
                    {r.subscription_status === "active" && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Ativo</Badge>}
                    {(!r.subscription_status || r.subscription_status === "trial") && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Trial</Badge>}
                    {r.subscription_status === "suspended" && <Badge className="bg-red-100 text-red-700 text-[10px]">Suspenso</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{r.email} {r.phone && `| ${r.phone}`}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-bold">{r.total_orders} pedidos</p>
                  <p className="text-xs text-emerald-600 font-medium">{r.total_revenue?.toFixed(2)} EUR</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs gap-1 h-8 border-emerald-200 text-emerald-700 hover:bg-emerald-50 flex-shrink-0"
                  onClick={(e) => { e.stopPropagation(); onLoginAs(r.id, r.name); }}
                  disabled={impersonating}
                  data-testid={`login-as-${r.id?.slice(-6)}`}
                >
                  <LogIn className="w-3 h-3" /> Entrar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-xs h-8 text-red-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!window.confirm(`ATENÇÃO: Eliminar "${r.name}" permanentemente? Todos os dados serão perdidos.`)) return;
                    try {
                      await adminAxios.delete(`/platform-admin/restaurants/${r.id}`);
                      toast.success(`${r.name} eliminado`);
                      onRefreshTab("restaurants");
                    } catch { toast.error("Erro ao eliminar"); }
                  }}
                  data-testid={`delete-rest-${r.id?.slice(-6)}`}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
                <ChevronRight
                  className="w-4 h-4 text-muted-foreground flex-shrink-0 cursor-pointer"
                  onClick={() => onOpenDetail(r.id)}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
